import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Button, Card, Col, DatePicker, Descriptions, Form, Input, Row, Select, Space, Table, Tag, Typography } from 'antd';
import { ReloadOutlined, SyncOutlined } from '@ant-design/icons';
import moment from 'moment';

import TableActionBar from '@/components/TableActionBar';
import DynamicTable from '@/components/DynamicTable';
import BaseSelect from '@/components/Elements/BaseSelect';
import { fetchCommissionIneligibleServices, recalculateCommission } from '@/redux/actions/commissionActions';
import { getPagination } from '@/utils/helpers';
import { renderServiceGroup } from '@/utils/serviceGroups';
import { checkPermission } from '@/utils/permission';
import { money } from './commissionLifecycleHelpers';
import CommissionRelatedEmployees from './CommissionRelatedEmployees';
import { CommissionCustomerColumn, CommissionServiceColumn } from './CommissionCustomerServiceColumns';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const serviceStatuses = [
    { value: 'chualam', label: 'Chưa thực hiện' },
    { value: 'dadatcoc', label: 'Đã đặt cọc' },
    { value: 'danglam', label: 'Đang thực hiện' },
    { value: 'dahoanthanh', label: 'Đã hoàn thành' },
];
const commissionStatuses = [
    { value: 'pending', label: 'Chưa tính' },
    { value: 'calculated', label: 'Đã chạy tính' },
    { value: 'failed', label: 'Tính lỗi' },
];
const commissionStatusLabels = { pending: 'Chưa tính', calculated: 'Đã chạy tính', failed: 'Tính lỗi' };
const sourceTypeLabels = {
    MANUAL: 'Cập nhật thủ công',
    CUSTOMER_IMAGE: 'Ảnh khách hàng',
    IMAGE: 'Ảnh khách hàng',
    TREATMENT_SESSION: 'Buổi điều trị',
    MEDICAL_RECORD: 'Hồ sơ bệnh án',
    ORTHODONTIC_PLAN: 'Kế hoạch chỉnh nha',
};
const dateTime = value => value ? moment(value).format('DD/MM/YYYY HH:mm') : '—';

export default function CommissionIneligibleServicesTab({ onTotalChange }) {
    const dispatch = useDispatch();
    const config = useSelector(state => state.config || {});
    const [filterForm] = Form.useForm();
    const [result, setResult] = useState({ data: [], total: 0, current_page: 1, per_page: 15, filter_options: {} });
    const [loading, setLoading] = useState(false);
    const [recalculatingId, setRecalculatingId] = useState(null);
    const allowCalculate = checkPermission('commission_result', 'calculate');

    const buildFilters = useCallback((page, perPage) => {
        const values = filterForm.getFieldsValue();
        return {
            keyword: values.keyword,
            service_group_id: values.service_group_id,
            service_status: values.service_status,
            commission_status: values.commission_status,
            branch_id: values.branch_id,
            employee_id: values.employee_id,
            role_key: values.role_key,
            condition_code: values.condition_code,
            source_status: values.source_status,
            period_key: values.period_key?.format('YYYY-MM'),
            from_date: values.selected_at?.[0]?.format('YYYY-MM-DD'),
            to_date: values.selected_at?.[1]?.format('YYYY-MM-DD'),
            page,
            per_page: perPage,
        };
    }, [filterForm]);

    const loadPage = useCallback(async (page = 1, perPage = 15) => {
        setLoading(true);
        try {
            const response = await dispatch(fetchCommissionIneligibleServices(buildFilters(page, perPage)));
            if (!response) return;
            setResult(response);
            if (onTotalChange) onTotalChange(response.total || 0);
        } catch (error) { /* Action đã hiển thị lỗi. */ }
        finally { setLoading(false); }
    }, [buildFilters, dispatch, onTotalChange]);

    useEffect(() => { loadPage(); }, [loadPage]);

    const recalculate = async item => {
        setRecalculatingId(item.customer_service_id);
        try {
            await dispatch(recalculateCommission(item.customer_service_id));
            await loadPage(result.current_page || 1, result.per_page || 15);
        } finally { setRecalculatingId(null); }
    };

    const columns = [
        {
            title: 'Khách hàng / dịch vụ',
            key: 'customer_service',
            width: 310,
            render: (_, item) => <Space direction="vertical" size={2}>
                <CommissionCustomerColumn customer={item.customer} />
                <div>{renderServiceGroup(item.service?.group) || <Tag>Chưa xác định nhóm</Tag>}</div>
                <CommissionServiceColumn customerServiceId={item.customer_service_id} service={item.service} />
                <Text type="secondary">Giá trị: {money(item.service_amount)}</Text>
            </Space>,
        },
        {
            title: 'Trạng thái dịch vụ',
            key: 'service_status',
            width: 170,
            render: (_, item) => <Space direction="vertical" size={2}>
                <Tag>{item.service_status_label}</Tag>
                <Text type="secondary">Hoa hồng: {commissionStatusLabels[item.commission_status] || item.commission_status || 'Chưa tính'}</Text>
                <Text type="secondary">Ngày chốt: {dateTime(item.selected_at)}</Text>
            </Space>,
        },
        {
            title: 'Nội dung chưa đủ',
            key: 'requirements',
            width: 360,
            render: (_, item) => <Space direction="vertical" size={4}>
                {(item.requirements || []).slice(0, 3).map((requirement, index) => (
                    <div key={`${requirement.type}-${requirement.code || index}`}>
                        <Tag color="red">{requirement.label}</Tag>
                        <Text type="secondary">{requirement.action}</Text>
                    </div>
                ))}
                {(item.requirements || []).length > 3 && <Text type="secondary">Còn {(item.requirements || []).length - 3} nội dung — mở dòng để xem đầy đủ.</Text>}
            </Space>,
        },
        {
            title: 'Nhân viên liên quan',
            dataIndex: 'related_employees',
            key: 'related_employees',
            width: 260,
            render: value => <CommissionRelatedEmployees employees={value} />,
        },
        {
            title: 'Lần đánh giá gần nhất',
            key: 'evaluation',
            width: 190,
            render: (_, item) => item.latest_run ? <Space direction="vertical" size={2}>
                <Text>{dateTime(item.latest_run.created_at)}</Text>
                <Text type="secondary">Kỳ đánh giá: {moment(item.latest_run.period_key, 'YYYY-MM').format('MM/YYYY')}</Text>
                <Tag color={item.latest_run.status === 'FAILED' ? 'red' : 'blue'}>{item.latest_run.status}</Tag>
            </Space> : <Text type="secondary">Chưa có lần đánh giá</Text>,
        },
        {
            title: 'Thao tác',
            key: 'action',
            fixed: 'right',
            width: 105,
            render: (_, item) => allowCalculate && <Button size="small" type="primary" icon={<SyncOutlined />} loading={recalculatingId === item.customer_service_id} onClick={() => recalculate(item)}>Tính lại</Button>,
        },
    ];

    const conditionColumns = [
        { title: 'Điều kiện', key: 'condition', width: 250, render: (_, condition) => <><Text strong>{condition.name}</Text><br /><Text type="secondary">{condition.code}</Text></> },
        { title: 'Bắt buộc', dataIndex: 'required', width: 95, render: value => <Tag color={value ? 'blue' : 'default'}>{value ? 'Có' : 'Không'}</Tag> },
        { title: 'Trạng thái', key: 'status', width: 150, render: (_, condition) => <Tag color={condition.is_missing ? 'red' : (condition.is_satisfied ? 'green' : 'default')}>{condition.is_missing ? 'Còn thiếu' : (condition.is_satisfied ? 'Đã đạt' : 'Chưa đạt')}</Tag> },
        { title: 'Nguồn dữ liệu', key: 'source', width: 170, render: (_, condition) => <><div>{sourceTypeLabels[condition.source_type] || condition.source_type || 'Chưa có nguồn'}</div>{condition.read_only && <Text type="secondary">Tự động cập nhật</Text>}</> },
        { title: 'Cần cập nhật', key: 'action', width: 330, render: (_, condition) => condition.is_missing ? <><Text>{condition.missing_reason}</Text><br /><Text type="secondary">{condition.action}</Text></> : '—' },
    ];

    const expandedRowRender = item => <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Row gutter={[12, 12]}>
            {(item.requirements || []).map((requirement, index) => <Col xs={24} lg={12} key={`${requirement.type}-${requirement.code || index}`}>
                <Card size="small" style={{ height: '100%' }}>
                    <Text strong>{requirement.label}</Text><br />
                    <Text>{requirement.detail}</Text><br />
                    <Text type="secondary">Cần làm: {requirement.action}</Text>
                </Card>
            </Col>)}
        </Row>
        <Text strong>Điều kiện theo snapshot của lần đánh giá</Text>
        <Table rowKey="code" size="small" pagination={false} dataSource={item.conditions || []} columns={conditionColumns} scroll={{ x: 995 }} />
        <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="Nguồn doanh thu">{money(item.financial?.source_revenue || 0)}</Descriptions.Item>
            <Descriptions.Item label="Doanh thu đủ điều kiện">{money(item.financial?.eligible_revenue || 0)}</Descriptions.Item>
            <Descriptions.Item label="Nhân viên liên quan" span={2}>
                <CommissionRelatedEmployees employees={item.related_employees} />
            </Descriptions.Item>
            <Descriptions.Item label="Mốc xác định kỳ">{item.period_basis?.label || 'Chưa có'}</Descriptions.Item>
            <Descriptions.Item label="Ngày xác định kỳ">{dateTime(item.period_basis?.date)}</Descriptions.Item>
            <Descriptions.Item label="Giải thích kỳ" span={2}>{item.period_basis?.explanation || 'Chưa có lần đánh giá hoặc khoản hoa hồng để xác định kỳ.'}</Descriptions.Item>
        </Descriptions>
    </Space>;

    const serviceGroups = result.filter_options?.service_groups || [];
    const conditions = result.filter_options?.conditions || [];

    return <div>
        <Alert type="info" style={{ marginBottom: 16 }} message="Danh sách chỉ gồm dịch vụ mà lần chạy tính hoa hồng gần nhất đã ghi nhận bị chặn do thiếu điều kiện. Điều kiện hiển thị theo bản ghi của lần chạy đó; sau khi bổ sung dữ liệu, hãy tính lại để cập nhật trạng thái." />
        <Form form={filterForm} component={false}>
            <TableActionBar isShowAddButton={false} isShowDeleteButton={false} showSearch={false} showFilter={false}>
                <Form.Item name="keyword" noStyle><Input allowClear placeholder="Mã/tên khách hàng hoặc dịch vụ" className="table-button" style={{ width: 250 }} /></Form.Item>
                <Form.Item name="selected_at" noStyle><RangePicker format="DD/MM/YYYY" placeholder={['Chọn dịch vụ từ', 'Đến ngày']} className="table-button" /></Form.Item>
                <Form.Item name="branch_id" noStyle><Select allowClear showSearch optionFilterProp="label" placeholder="Chi nhánh" className="table-button" style={{ width: 170 }} options={(config.branches || []).map(branch => ({ value: branch.id, label: branch.name || branch.full_name }))} /></Form.Item>
                <Form.Item name="employee_id" noStyle>
                    <BaseSelect options={config.users || []} optionValue="user_id" optionLabel="display_name"
                        showSearch placeholder="Nhân viên liên quan" className="table-button" style={{ width: 240 }} />
                </Form.Item>
                <Form.Item name="role_key" noStyle><Select allowClear placeholder="Vai trò hưởng" className="table-button" style={{ width: 160 }} options={[{ value: 'doctor', label: 'Bác sĩ' }, { value: 'sales', label: 'Sales' }]} /></Form.Item>
                <Form.Item name="service_group_id" noStyle><Select allowClear showSearch optionFilterProp="label" placeholder="Nhóm dịch vụ" className="table-button" style={{ width: 180 }} options={serviceGroups.map(group => ({ value: group.id, label: group.name }))} /></Form.Item>
                <Form.Item name="service_status" noStyle><Select allowClear placeholder="Trạng thái dịch vụ" className="table-button" style={{ width: 180 }} options={serviceStatuses} /></Form.Item>
                <Form.Item name="commission_status" noStyle><Select allowClear placeholder="Trạng thái commission" className="table-button" style={{ width: 180 }} options={commissionStatuses} /></Form.Item>
                <Form.Item name="condition_code" noStyle><Select allowClear showSearch optionFilterProp="label" placeholder="Điều kiện còn thiếu" className="table-button" style={{ width: 220 }} options={conditions.map(condition => ({ value: condition.code, label: condition.name }))} /></Form.Item>
                <Form.Item name="source_status" noStyle><Select allowClear placeholder="Nguồn doanh thu" className="table-button" style={{ width: 175 }} options={[{ value: 'HAS_SOURCE', label: 'Đã có nguồn thu' }, { value: 'NO_SOURCE', label: 'Chưa có nguồn thu' }]} /></Form.Item>
                <Form.Item name="period_key" noStyle><DatePicker picker="month" format="MM/YYYY" placeholder="Kỳ đánh giá" className="table-button" /></Form.Item>
                <Button type="primary" onClick={() => loadPage(1, result.per_page || 15)} className="table-button">Lọc</Button>
                <Button onClick={() => { filterForm.resetFields(); setTimeout(() => loadPage(1, result.per_page || 15), 0); }} className="table-button">Xóa lọc</Button>
                <Button icon={<ReloadOutlined />} loading={loading} onClick={() => loadPage(result.current_page || 1, result.per_page || 15)} className="table-button">Làm mới</Button>
            </TableActionBar>
        </Form>
        <DynamicTable
            rowKey="customer_service_id"
            rowSelection={false}
            moduleKey="commission-ineligible-services"
            isLoading={loading}
            dataSource={result.data || []}
            columns={columns}
            expandable={{ expandedRowRender }}
            pagination={getPagination(result, 15)}
            onChange={pagination => loadPage(pagination.current, pagination.pageSize)}
            scroll={{ x: 1395 }}
            locale={{ emptyText: 'Không có dịch vụ chưa đủ điều kiện trong phạm vi lọc hiện tại.' }}
        />
    </div>;
}
